import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Info } from "lucide-react";
import EntrevueClientPanel from "./EntrevueClientPanel";
import "./FIR.css";
import { buildVisibleSequence } from "./firVisibility";
import { REGION_BUREAUX } from "./regionBureaux";
import { REGION_TO_LEGACY_FIELD } from "./regionLegacy";
import { formatNAS, validateNAS, formatCodePostal, formatNomPropre } from "../utils/formatters";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Les questions _region / _bureau font partie du schéma (module de gestion).
// Le wizard calcule seulement dynamiquement les choix de bureau selon la région.

// Fields asked at FIR creation time — we don't re-ask them in the wizard.
// They are still kept in `answers` so conditional rules like "Nom is not null" work.
const CREATION_FIELDS = new Set(["Nom", "Pr_nom", "Date_de_naissance1"]);

// Âge (en années) à partir d'une date de naissance AAAA-MM-JJ.
function computeAge(ddn) {
  if (!ddn) return null;
  const d = new Date(ddn);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a;
}

// Detect NAS (Numéro d'Assurance Sociale) fields by name or label
function isNASField(field) {
  const n = (field.name || "").toLowerCase();
  const d = (field.displayname || "").toLowerCase();
  return (
    n.includes("assurance_social") ||
    n.includes("num_ro_d_assurance") ||
    n === "nas" ||
    d.includes("assurance sociale") ||
    d.includes("n.a.s")
  );
}

// Format téléphone : ###-###-####
function formatTelephoneDashes(value) {
  if (!value) return "";
  const d = value.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
}

function formatDateAAAAMMJJ(raw) {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}

// Champs texte qui sont en réalité des dates : help_text "AAAA/MM/JJ" dans le schéma,
// ou libellé/nom de champ date (ex. 2.1.8.x Immigration : « Date d'arrivée au Canada »)
function isDateLikeField(field) {
  if (/AAAA/i.test(field.help_text || "")) return true;
  if (field.type !== "text") return false;
  const n = field.name || "";
  const d = field.displayname || "";
  return /^date_/i.test(n) || /(^|[^a-zà-ÿ])date\b/i.test(d);
}

// Validation d'une date AAAA-MM-JJ complète et réelle
function validateDateStr(v) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return { valid: false, message: "Date incomplète — tapez les 8 chiffres (AAAAMMJJ)" };
  const [y, m, d] = v.split("-").map(Number);
  if (y < 1900 || y > 2100) return { valid: false, message: "Année invalide" };
  if (m < 1 || m > 12) return { valid: false, message: "Mois invalide (01 à 12)" };
  const dim = new Date(y, m, 0).getDate();
  if (d < 1 || d > dim) return { valid: false, message: "Jour invalide pour ce mois" };
  return { valid: true, message: "" };
}

// Sous-champ de grille qui est une date
function isGridDateSubfield(sf) {
  return (sf.displayname || "").toLowerCase().includes("date") ||
         sf.name.toLowerCase().startsWith("date_");
}

// Detect Canadian postal code fields by name or label
function isPostalCodeField(field) {
  const n = (field.name || "").toLowerCase();
  const d = (field.displayname || "").toLowerCase();
  return (
    n === "code_postal" || n.includes("code_postal") ||
    d.includes("code postal")
  );
}

// Detect phone fields
function isPhoneField(field) {
  if (field.type === "phonenumber") return true;
  const n = (field.name || "").toLowerCase();
  const d = (field.displayname || "").toLowerCase();
  return n.startsWith("t_l_phone") || d.startsWith("téléphone") || d.includes("téléphone");
}

// Fields where Title Case should NOT be applied (numeric / format-specific)
function skipTitleCase(field) {
  if (isNASField(field) || isPostalCodeField(field) || isPhoneField(field)) return true;
  if (field.type === "email") return true;
  if (field.type === "number" || field.type === "decimal" || field.type === "CAD") return true;
  const n = (field.name || "").toLowerCase();
  const d = (field.displayname || "").toLowerCase();
  // Numeric-only text fields
  if (n.startsWith("num_ro") || n.startsWith("n_") || n.includes("nas")) return true;
  if (d.startsWith("numéro") || d.startsWith("nº") || d.includes("n°")) return true;
  return false;
}

// Sous-formulaire grille : permet d'entrer plusieurs lignes de données structurées
// (ex: liste des enfants à charge avec nom, prénom, date de naissance)
function GridSubform({ field, value, onChange }) {
  const subfields = (field.subfields || []).filter(sf => sf.name !== "Nombre_d_enfant2");
  const MAX_ROWS = 10;
  const rows = value && value.length > 0 ? value : [{}];

  const updateRow = (idx, key, val) => {
    const next = rows.map((r, i) => i === idx ? { ...r, [key]: val } : r);
    onChange(next);
  };
  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    onChange([...rows, {}]);
  };
  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? [{}] : next);
  };

  const formatDateFlex = (raw) => {
    if (!raw) return "";
    const d = raw.replace(/\D/g, "").slice(0, 8);
    if (d.length <= 4) return d;
    if (d.length <= 6) return `${d.slice(0,4)}-${d.slice(4)}`;
    return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6)}`;
  };

  return (
    <div data-testid={`fir-grid-${field.name}`} style={{ width: "100%", maxWidth: 800 }}>
      <table className="fir-grid-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}>#</th>
            {subfields.map(sf => (
              <th key={sf.name}>{sf.displayname || sf.name}</th>
            ))}
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td style={{ textAlign: "center", color: "#666" }}>{idx + 1}</td>
              {subfields.map(sf => {
                const val = row[sf.name] || "";
                const isDate = (sf.displayname || "").toLowerCase().includes("date") ||
                               sf.name.toLowerCase().startsWith("date_");
                const isText = sf.type === "text" && !isDate;
                const dateInvalid = isDate && val.length === 10 && !validateDateStr(val).valid;
                return (
                  <td key={sf.name}>
                    <input
                      className="fir-input"
                      style={{ padding: "4px 6px", fontSize: 12,
                               ...(dateInvalid ? { borderColor: "#cc0000", background: "#fff4f4" } : {}) }}
                      data-testid={`fir-grid-${field.name}-${idx}-${sf.name}`}
                      type="text"
                      value={val}
                      placeholder=""
                      maxLength={isDate ? 10 : (sf.maxchar || undefined)}
                      inputMode={isDate ? "numeric" : undefined}
                      onChange={e => {
                        let v = e.target.value;
                        if (isDate) v = formatDateFlex(v);
                        updateRow(idx, sf.name, v);
                      }}
                      onBlur={e => {
                        if (isText && e.target.value) {
                          updateRow(idx, sf.name, formatNomPropre(e.target.value));
                        }
                      }}
                    />
                  </td>
                );
              })}
              <td style={{ textAlign: "center" }}>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="win-btn"
                    style={{ fontSize: 11, padding: "2px 8px", color: "#cc0000" }}
                    onClick={() => removeRow(idx)}
                    data-testid={`fir-grid-${field.name}-remove-${idx}`}
                    title="Supprimer cette ligne"
                  >×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          className="win-btn"
          onClick={addRow}
          disabled={rows.length >= MAX_ROWS}
          data-testid={`fir-grid-${field.name}-add`}
        >
          + Ajouter une ligne
        </button>
        <span style={{ color: "#666", fontSize: 12 }}>
          {rows.length} / {MAX_ROWS} ligne{rows.length > 1 ? "s" : ""}
        </span>
        {(() => {
          for (let ri = 0; ri < rows.length; ri++) {
            for (const sf of subfields) {
              const isDate = (sf.displayname || "").toLowerCase().includes("date") ||
                             sf.name.toLowerCase().startsWith("date_");
              const v = rows[ri][sf.name] || "";
              if (isDate && v.length === 10) {
                const res = validateDateStr(v);
                if (!res.valid) {
                  return (
                    <span style={{ color: "#cc0000", fontSize: 12 }} data-testid={`fir-grid-${field.name}-date-error`}>
                      ⚠ Ligne {ri + 1} : {res.message}
                    </span>
                  );
                }
              }
            }
          }
          return null;
        })()}
      </div>
    </div>
  );
}

// 1.2.20.1 « Quel endroit ? » : liste des bureaux Static (confweb=1)
function BureauxWebSelect({ field, value, onChange }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    axios.get(`${API}/reference/bureaux-web`)
      .then(r => setOptions(r.data || []))
      .catch(() => setOptions([]));
  }, []);
  return (
    <select
      className="fir-input"
      data-testid={`fir-select-${field.name}`}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">— Sélectionnez un bureau —</option>
      {options.map(o => (
        <option key={`${o.region}-${o.code}`} value={o.bureau}>{o.bureau}</option>
      ))}
    </select>
  );
}

function FieldRenderer({ field, value, onChange, onEnterNext, error, answers, onAnswer }) {
  const t = field.type;

  // Single-line input types: pressing Enter should advance to the next question
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && onEnterNext) {
      e.preventDefault();
      onEnterNext();
    }
  };

  // 1.2.20.1 « Quel endroit ? » : liste des bureaux (Static, confweb=1)
  if (field.name === "ENDROIT") {
    return <BureauxWebSelect field={field} value={value} onChange={onChange} />;
  }

  // NAS — Canadian Social Insurance Number with Luhn validation + XXX-XXX-XXX mask
  if (isNASField(field)) {
    return (
      <div>
        <input
          type="text"
          className="fir-input"
          data-testid={`fir-input-${field.name}`}
          value={value || ""}
          onChange={e => onChange(formatNAS(e.target.value))}
          onKeyDown={handleKeyDown}
          placeholder="XXX-XXX-XXX"
          maxLength={11}
          inputMode="numeric"
          style={error ? { borderColor: "#cc0000", background: "#fff4f4" } : {}}
        />
        {error && (
          <div style={{ color: "#cc0000", fontSize: 12, marginTop: 4 }}>
            ⚠ {error}
          </div>
        )}
      </div>
    );
  }

  // Postal code — Canadian format A1A 1A1
  if (isPostalCodeField(field)) {
    return (
      <input
        type="text"
        className="fir-input"
        data-testid={`fir-input-${field.name}`}
        value={value || ""}
        onChange={e => onChange(formatCodePostal(e.target.value))}
        onKeyDown={handleKeyDown}
        placeholder="A1A 1A1"
        maxLength={7}
        style={{ textTransform: "uppercase" }}
      />
    );
  }

  if (t === "text" || t === "email" || t === "phonenumber" || t === "name" ||
      t === "first_name" || t === "last_name") {
    // Champs texte de type date (help_text "AAAA/MM/JJ") : saisie libre 8 chiffres → AAAA-MM-JJ
    if (isDateLikeField(field)) {
      const selfErr = (value && value.length === 10 && !validateDateStr(value).valid)
        ? validateDateStr(value).message : null;
      const shownErr = error || selfErr;
      return (
        <div>
          <input
            type="text"
            className="fir-input"
            data-testid={`fir-input-${field.name}`}
            value={value || ""}
            onChange={e => onChange(formatDateAAAAMMJJ(e.target.value))}
            onKeyDown={handleKeyDown}
            maxLength={10}
            inputMode="numeric"
            style={shownErr ? { borderColor: "#cc0000", background: "#fff4f4" } : {}}
          />
          {shownErr && (
            <div style={{ color: "#cc0000", fontSize: 12, marginTop: 4 }}>⚠ {shownErr}</div>
          )}
        </div>
      );
    }
    const isPhone = isPhoneField(field);
    const applyTitle = !skipTitleCase(field);
    return (
      <div>
        <input
          type={t === "email" ? "email" : (isPhone ? "tel" : "text")}
          className="fir-input"
          data-testid={`fir-input-${field.name}`}
          value={value || ""}
          onChange={e => onChange(isPhone ? formatTelephoneDashes(e.target.value) : e.target.value)}
          onBlur={e => {
            if (applyTitle && e.target.value) {
              onChange(formatNomPropre(e.target.value));
            }
          }}
          onKeyDown={handleKeyDown}
          maxLength={field.maxchar || undefined}
          inputMode={isPhone ? "tel" : undefined}
          style={error ? { borderColor: "#cc0000", background: "#fff4f4" } : {}}
        />
        {error && (
          <div style={{ color: "#cc0000", fontSize: 12, marginTop: 4 }}>
            ⚠ {error}
          </div>
        )}
      </div>
    );
  }
  if (t === "textarea") {
    return (
      <textarea
        className="fir-textarea"
        data-testid={`fir-input-${field.name}`}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (t === "number" || t === "decimal" || t === "CAD") {
    return (
      <input
        type="number"
        className="fir-input"
        data-testid={`fir-input-${field.name}`}
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        step={t === "decimal" || t === "CAD" ? "0.01" : "1"}
      />
    );
  }
  if (t === "date") {
    // Toutes les dates : saisie libre des 8 chiffres (AAAAMMJJ) → auto-format AAAA-MM-JJ
    const selfErr = (value && value.length === 10 && !validateDateStr(value).valid)
      ? validateDateStr(value).message : null;
    const shownErr = error || selfErr;
    return (
      <div>
        <input
          type="text"
          className="fir-input"
          data-testid={`fir-input-${field.name}`}
          value={value || ""}
          onChange={e => onChange(formatDateAAAAMMJJ(e.target.value))}
          onKeyDown={handleKeyDown}
          maxLength={10}
          inputMode="numeric"
          style={shownErr ? { borderColor: "#cc0000", background: "#fff4f4" } : {}}
        />
        {shownErr && (
          <div style={{ color: "#cc0000", fontSize: 12, marginTop: 4 }}>⚠ {shownErr}</div>
        )}
      </div>
    );
  }
  if (t === "radiobuttons") {
    const options = field.values || ["Oui", "Non"];
    return (
      <div className="fir-radio-group" data-testid={`fir-radio-${field.name}`}>
        {options.map(opt => (
          <label key={opt}>
            <input
              type="radio"
              name={field.name}
              checked={value === opt}
              onChange={() => onChange(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (t === "checkboxes") {
    const options = field.values || [];
    const arr = Array.isArray(value) ? value : [];
    // Montant en $ sous chaque option cochée : 3.2 (sauf 27- Aucun revenu) et 5.1 (sauf 1- Aucun)
    const MONTANT_FIELDS = {
      "Quelles_sont_vos_sources_de_revenus_et_ou_avantages_financiers_depuis_le_1er_de_janvier_de_l_ann_e": /^27-/,
      "Vos_actifs_et_dettes": /^1-\s/,
    };
    const montantExclu = MONTANT_FIELDS[field.name];
    const hasMontant = field.demande_montant === true || montantExclu !== undefined;
    const montKey = `${field.name}_montants`;
    const montants = (hasMontant && answers && answers[montKey] && typeof answers[montKey] === "object") ? answers[montKey] : {};
    const sansMontant = (opt) => (montantExclu ? montantExclu.test(opt) : false);
    const toggle = (opt) => {
      if (arr.includes(opt)) {
        onChange(arr.filter(x => x !== opt));
        if (hasMontant && onAnswer && montants[opt] !== undefined) {
          const next = { ...montants };
          delete next[opt];
          onAnswer(montKey, next);
        }
      } else {
        onChange([...arr, opt]);
      }
    };
    return (
      <div className="fir-check-group" data-testid={`fir-check-${field.name}`}>
        {options.map((opt, oi) => (
          <div key={opt}>
            <label>
              <input
                type="checkbox"
                checked={arr.includes(opt)}
                onChange={() => toggle(opt)}
              />
              <span>{opt}</span>
            </label>
            {hasMontant && arr.includes(opt) && !sansMontant(opt) && (
              <div style={{ margin: "2px 0 8px 28px" }}>
                <input
                  className="fir-input"
                  style={{ width: 160 }}
                  inputMode="numeric"
                  data-testid={`fir-montant-${field.name}-${oi}`}
                  value={montants[opt] ? `${Number(montants[opt]).toLocaleString("fr-CA")} $` : ""}
                  placeholder="Montant $"
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                    onAnswer && onAnswer(montKey, { ...montants, [opt]: digits });
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  if (t === "checkbox") {
    return (
      <label className="fir-check-group">
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          data-testid={`fir-check-${field.name}`}
        />
        <span>{field.displayname}</span>
      </label>
    );
  }
  if (t === "picklist") {
    const options = field.values || [];
    return (
      <select
        className="fir-input"
        data-testid={`fir-select-${field.name}`}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">— Sélectionnez —</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (t === "list" || t === "grid") {
    return (
      <div>
        <GridSubform
          field={field}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
        />
        {error && (
          <div style={{ color: "#cc0000", fontSize: 12, marginTop: 6 }}>
            ⚠ {error}
          </div>
        )}
      </div>
    );
  }
  // fallback
  return (
    <input
      className="fir-input"
      data-testid={`fir-input-${field.name}`}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
    />
  );
}

export default function FIRWizard({ firId, onClose, onCompleted }) {
  const [schema, setSchema] = useState(null);
  const [fir, setFir] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fieldError, setFieldError] = useState("");
  const saveTimerRef = useRef(null);
  const activeCardRef = useRef(null);
  // Track active session time: increment total_seconds at each save with the
  // delta since the previous save (or wizard mount).
  const lastTickRef = useRef(Date.now());

  // Compute and reset the delta (in seconds) since last save
  const popDelta = useCallback(() => {
    const now = Date.now();
    const delta = Math.round((now - lastTickRef.current) / 1000);
    lastTickRef.current = now;
    return delta > 0 ? delta : 0;
  }, []);

// Types qui avancent automatiquement après réponse (1 valeur unique, clic suffit)
  // Note: les dates ne sont PAS dans cette liste — l'avance se fait au blur ou à Enter
  // pour éviter qu'une navigation au clavier dans le datepicker déclenche l'avance.
  const AUTO_ADVANCE_TYPES = new Set([
    "radiobuttons", "picklist",
  ]);

  // Load schema + FIR data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [schemaRes, firRes] = await Promise.all([
          axios.get(`${API}/fir/schema`),
          axios.get(`${API}/fir/${firId}`)
        ]);
        if (cancelled) return;
        setSchema(schemaRes.data);
        setFir(firRes.data);
        // Hydrate the virtual _region / _bureau fields from the legacy bureau picklist
        // that has a value (so resume positions correctly past them).
        const loaded = { ...(firRes.data.answers || {}) };
        if (!loaded._region || !loaded._bureau) {
          for (const [region, legacyName] of Object.entries(REGION_TO_LEGACY_FIELD)) {
            if (loaded[legacyName]) {
              loaded._region = region;
              loaded._bureau = loaded[legacyName];
              break;
            }
          }
        }
        setAnswers(loaded);
      } catch (e) {
        console.error(e);
        alert("Erreur de chargement du FIR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firId]);

  // Build visible sequence (recomputed when answers change)
  const sequence = useMemo(() => {
    if (!schema) return [];
    // Les questions _region / _bureau font désormais partie du schéma (module
    // de gestion). On calcule seulement dynamiquement les choix de bureau selon
    // la région sélectionnée, et on retire les champs saisis à la création.
    const patched = {
      ...schema,
      sections: schema.sections.map((sec) => {
        let fields = (sec.fields || []).filter(f => !CREATION_FIELDS.has(f.name));
        fields = fields.map(f => {
          if (f.name === "_bureau") {
            const region = answers._region || "";
            return { ...f, values: region ? (REGION_BUREAUX[region] || []) : [] };
          }
          return f;
        });
        return { ...sec, fields };
      })
    };
    return buildVisibleSequence(patched, answers);
  }, [schema, answers]);

  // Auto-scroll the newly-revealed question into view & focus its first input
  const firstScrollDoneRef = useRef(false);
  useEffect(() => {
    if (!activeCardRef.current) return;
    // First scroll on mount: instant (no animation) so the user lands directly
    // on their last position. Subsequent scrolls during interaction: smooth.
    const behavior = firstScrollDoneRef.current ? "smooth" : "auto";
    // Wait one frame so layout is fully computed (especially after data load).
    requestAnimationFrame(() => {
      if (activeCardRef.current) {
        activeCardRef.current.scrollIntoView({ behavior, block: "center" });
        firstScrollDoneRef.current = true;
      }
    });
    // Skip radio buttons & checkboxes — they would visually appear pre-selected
    // because of the focus ring, which the user could mistake for a default value.
    const firstInput = activeCardRef.current.querySelector(
      "input[type=text]:not([disabled]), input[type=email]:not([disabled]), input[type=tel]:not([disabled]), input[type=number]:not([disabled]), input[type=date]:not([disabled]), input:not([type]):not([disabled]), select:not([disabled]), textarea:not([disabled])"
    );
    if (firstInput) {
      setTimeout(() => firstInput.focus({ preventScroll: true }), 300);
    }
  }, [currentIdx]);

  // Helper : returns true if the field has an answer (non-empty)
  const hasAnswer = useCallback((fieldName) => {
    const v = answers[fieldName];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  }, [answers]);

  // After loading, jump to the position right after the LAST answered question.
  // This is more robust than "first unanswered" because conditional rules can reveal
  // earlier questions that the user skipped — we don't want to jump backwards.
  const initialJumpDone = useRef(false);
  useEffect(() => {
    if (!fir || !sequence.length || initialJumpDone.current) return;
    let lastAnswered = -1;
    for (let i = 0; i < sequence.length; i++) {
      const item = sequence[i];
      if (item.type === "field" && hasAnswer(item.field.name)) {
        lastAnswered = i;
      }
    }
    let target;
    if (lastAnswered >= 0) {
      target = Math.min(lastAnswered + 1, sequence.length - 1);
    } else {
      target = sequence[0]?.type === "section" ? 1 : 0;
    }
    // Skip past section markers — we want to land on a real question card
    while (target < sequence.length - 1 && sequence[target]?.type === "section") {
      target += 1;
    }
    setCurrentIdx(target);
    initialJumpDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fir, sequence.length]);

  // Find the section title for the current step
  const currentItem = sequence[currentIdx];

  // La séquence est dynamique : un index sauvegardé peut retomber sur un marqueur
  // de section ou hors bornes → plus de carte active ni de bouton Continuer.
  // Normaliser : toujours pointer sur un champ (forme fonctionnelle pour éviter
  // toute course avec le saut initial).
  useEffect(() => {
    if (!sequence.length) return;
    setCurrentIdx(prev => {
      if (prev >= sequence.length) {
        let j = sequence.length - 1;
        while (j >= 0 && sequence[j]?.type === "section") j--;
        return j >= 0 ? j : prev;
      }
      if (sequence[prev]?.type !== "section") return prev;
      let i = prev + 1;
      while (i < sequence.length && sequence[i]?.type === "section") i++;
      if (i < sequence.length) return i;
      let j = prev - 1;
      while (j >= 0 && sequence[j]?.type === "section") j--;
      return j >= 0 ? j : prev;
    });
  }, [sequence, currentIdx]);
  const fieldsSoFar = useMemo(() => {
    return sequence.slice(0, currentIdx + 1).filter(s => s.type === "field").length;
  }, [sequence, currentIdx]);
  const totalFields = useMemo(
    () => sequence.filter(s => s.type === "field").length,
    [sequence]
  );

  // Ne jamais enregistrer une valeur invalide : les dates/courriels invalides sont vidés avant sauvegarde
  const fieldDefs = useMemo(() => {
    const m = {};
    (schema?.sections || []).forEach(s => (s.fields || []).forEach(f => { m[f.name] = f; }));
    return m;
  }, [schema]);

  const sanitizeAnswers = useCallback((ans) => {
    const clean = { ...ans };
    for (const [name, val] of Object.entries(ans)) {
      const f = fieldDefs[name];
      if (!f) continue;
      if (f.type === "email" && typeof val === "string" && val.trim() &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim())) {
        clean[name] = "";
        continue;
      }
      if ((f.type === "date" || isDateLikeField(f)) && typeof val === "string" && val.trim() &&
          !validateDateStr(val.trim()).valid) {
        clean[name] = "";
        continue;
      }
      if ((f.type === "grid" || f.type === "list") && Array.isArray(val)) {
        clean[name] = val.map(row => {
          const r = { ...(row || {}) };
          (f.subfields || []).forEach(sf => {
            if (isGridDateSubfield(sf) && typeof r[sf.name] === "string" && r[sf.name].trim() &&
                !validateDateStr(r[sf.name].trim()).valid) {
              r[sf.name] = "";
            }
          });
          return r;
        });
      }
    }
    return clean;
  }, [fieldDefs]);

  const persist = useCallback(async (extra = {}) => {
    if (!fir) return;
    try {
      setSaving(true);
      const delta = popDelta();
      const res = await axios.patch(`${API}/fir/${fir.id}`, {
        answers: sanitizeAnswers(answers),
        current_field_idx: currentIdx,
        delta_seconds: delta,
        ...extra
      });
      setFir(res.data);
      setLastSaved(new Date());
    } catch (e) {
      console.error("save fir", e);
    } finally {
      setSaving(false);
    }
  }, [fir, answers, currentIdx, popDelta, sanitizeAnswers]);

  // Debounced auto-save when answers change
  useEffect(() => {
    if (!fir || loading) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persist();
    }, 700);
    return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, currentIdx]);

  // Heartbeat: send a delta every 30s while wizard is open and tab visible.
  // Pause counting when tab is hidden (don't bill time the user is on another tab).
  useEffect(() => {
    if (!fir || loading) return;
    let intervalId = null;
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Resume: reset the tick so the gap while hidden is not counted
        lastTickRef.current = Date.now();
      } else {
        // Tab hidden: flush current delta then stop accumulating
        persist();
      }
    };
    intervalId = setInterval(() => {
      if (document.visibilityState === "visible") persist();
    }, 30000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fir, loading]);

  const onAnswer = (fieldName, value) => {
    setAnswers(prev => {
      const next = { ...prev, [fieldName]: value };
      // If the région changes, wipe the bureau and the legacy mirror
      if (fieldName === "_region" && prev._region !== value) {
        next._bureau = "";
        // Clear any previously-set legacy field
        const prevLegacy = REGION_TO_LEGACY_FIELD[prev._region];
        if (prevLegacy) next[prevLegacy] = "";
      }
      // Mirror _bureau into the legacy per-region picklist so that
      // the many Deluge rules like "LISTE_..._Abitibi_T_miscamingue is not null"
      // can evaluate correctly (unlocks 1.2.6, 1.2.7, etc.).
      if (fieldName === "_bureau") {
        const legacy = REGION_TO_LEGACY_FIELD[next._region];
        if (legacy) next[legacy] = value;
      }
      if (fieldName === "_region") {
        const legacy = REGION_TO_LEGACY_FIELD[value];
        if (legacy && next._bureau) next[legacy] = next._bureau;
      }
      return next;
    });
    // Auto-advance for single-choice field types
    const currentField = sequence[currentIdx]?.field;
    if (currentField && currentField.name === fieldName
        && AUTO_ADVANCE_TYPES.has(currentField.type)
        && value !== "" && value != null) {
      // slight delay so the user sees their selection before the jump
      setTimeout(() => { goNext(); }, 250);
    }
  };

  // Valide un champ répondu (courriel, date, dates de grille). Retourne un message ou null.
  const validateAnsweredField = (f) => {
    if (!f) return null;
    if (f.type === "email") {
      const val = (typeof answers[f.name] === "string" ? answers[f.name] : "").trim();
      if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) {
        return "Adresse courriel invalide (format attendu : nom@domaine.com)";
      }
    }
    if (f.type === "date" || isDateLikeField(f)) {
      const val = (typeof answers[f.name] === "string" ? answers[f.name] : "").trim();
      if (val) {
        const res = validateDateStr(val);
        if (!res.valid) return res.message;
      }
    }
    if (f.type === "grid" || f.type === "list") {
      const rows = Array.isArray(answers[f.name]) ? answers[f.name] : [];
      for (let ri = 0; ri < rows.length; ri++) {
        for (const sf of f.subfields || []) {
          if (!isGridDateSubfield(sf)) continue;
          const v = ((rows[ri] || {})[sf.name] || "").trim();
          if (v) {
            const res = validateDateStr(v);
            if (!res.valid) return `Ligne ${ri + 1} — ${sf.displayname || sf.name} : ${res.message}`;
          }
        }
      }
    }
    return null;
  };

  const goNext = async () => {
    // Validation: if current field is a NAS, enforce Luhn check
    const currentField = sequence[currentIdx]?.field;
    if (currentField && isNASField(currentField)) {
      const val = answers[currentField.name] || "";
      const res = validateNAS(val);
      if (!res.valid) {
        setFieldError(res.message);
        return;
      }
    }
    // Validation de TOUTES les réponses visibles jusqu'à la question courante
    // (couvre aussi les cartes passées modifiées après coup)
    for (let vi = 0; vi <= currentIdx && vi < sequence.length; vi++) {
      const entry = sequence[vi];
      if (!entry || entry.type === "section") continue;
      const msg = validateAnsweredField(entry.field);
      if (msg) {
        setCurrentIdx(vi);
        setFieldError(msg);
        return;
      }
    }
    setFieldError("");
    let i = currentIdx + 1;
    // skip section markers (they don't ask a question)
    while (i < sequence.length && sequence[i]?.type === "section") i++;
    if (i >= sequence.length) {
      // end reached
      await persist();
      const ok = window.confirm("Vous avez répondu à toutes les questions visibles. Marquer l'entrevue comme terminée ?");
      if (ok) {
        await axios.post(`${API}/fir/${fir.id}/complete`);
        if (onCompleted) onCompleted();
        if (onClose) onClose();
      }
      return;
    }
    setCurrentIdx(i);
  };

  const goPrev = () => {
    setFieldError("");
    let i = currentIdx - 1;
    while (i >= 0 && sequence[i]?.type === "section") i--;
    if (i < 0) i = 0;
    setCurrentIdx(i);
  };

  const quit = async () => {
    await persist();
    if (onClose) onClose();
  };

  if (loading || !schema || !fir) {
    return (
      <div className="fir-overlay">
        <div className="fir-modal">
          <div className="fir-titlebar">
            <span>Chargement de l'entrevue…</span>
          </div>
        </div>
      </div>
    );
  }

  // Build the "revealed" portion of the sequence: everything up to and
  // including the currently-active field.
  const revealedItems = sequence.slice(0, Math.min(currentIdx + 1, sequence.length));
  const progress = totalFields > 0 ? Math.round((fieldsSoFar / totalFields) * 100) : 0;
  const allAnswered = currentIdx >= sequence.length - 1
    && currentItem?.type === "field"
    && hasAnswer(currentItem.field.name);

  return (
    <div className="fir-overlay fir-overlay--with-panel">
      <EntrevueClientPanel fir={fir} answers={answers} />
      <div className="fir-modal">
        <div className="fir-titlebar">
          <span>
            {fir.titre || `Entrevue #${fir.id}`}
            {(fir.client_prenom || fir.client_nom) && (
              <span style={{ opacity: 0.85, fontWeight: 400, marginLeft: 10 }}>
                · {[fir.client_prenom, fir.client_nom].filter(Boolean).join(" ")}
                {computeAge(fir.client_date_naissance) != null && ` · ${computeAge(fir.client_date_naissance)} ans`}
              </span>
            )}
            {answers._selectionne && (
              <span style={{ marginLeft: 10, background: "#c47f00", color: "#fff", fontWeight: 700, fontSize: 12, padding: "2px 8px", borderRadius: 4 }} data-testid="fir-wizard-selectionne">
                📄 Sélectionné pour vérification
              </span>
            )}
          </span>
          <button onClick={quit} title="Quitter et reprendre plus tard" data-testid="fir-wizard-close">×</button>
        </div>
        <div className="fir-progress">
          <span>Question {fieldsSoFar} / {totalFields}</span>
          <div className="fir-progress-bar"><div style={{ width: `${progress}%` }} /></div>
          <span>{progress}%</span>
          {saving ? (
            <span style={{ color: "#666" }} data-testid="fir-saving-indicator">Enregistrement…</span>
          ) : lastSaved ? (
            <span style={{ color: "#1b7d3a", fontWeight: 600 }} data-testid="fir-saved-indicator">
              ✓ Sauvegardé {lastSaved.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
            </span>
          ) : null}
        </div>

        <div className="fir-stream" data-testid="fir-stream">
          {revealedItems.map((item, idx) => {
            if (item.type === "section") {
              return (
                <div key={`sec-${idx}`} className="fir-stream-section">
                  {item.section.displayname}
                </div>
              );
            }
            const f = item.field;
            const isActive = idx === currentIdx;
            return (
              <div
                key={`f-${f.name}-${idx}`}
                className={`fir-stream-card ${isActive ? "active" : "answered"}`}
                ref={isActive ? activeCardRef : null}
                data-testid={`fir-card-${f.name}`}
              >
                <div className="fir-stream-q">
                  {f.displayname || f.name}
                  {f.aide && (
                    <span className="fir-aide-bubble" data-testid={`fir-aide-${f.name}`}
                          tabIndex={0} role="button" aria-label="Explication">
                      <Info size={15} strokeWidth={2.4} aria-hidden="true" />
                      <span className="fir-aide-tooltip">{f.aide}</span>
                    </span>
                  )}
                </div>
                {f.help_text && !isDateLikeField(f) && <div className="fir-question-help">💡 {f.help_text}</div>}
                <FieldRenderer
                  field={f}
                  value={answers[f.name]}
                  onChange={(v) => { setFieldError(""); onAnswer(f.name, v); }}
                  onEnterNext={isActive ? goNext : null}
                  error={isActive ? fieldError : ""}
                  answers={answers}
                  onAnswer={onAnswer}
                />
                {isActive && !allAnswered && (
                  <div className="fir-card-actions">
                    <button
                      type="button"
                      className="fir-btn-next"
                      onClick={goNext}
                      data-testid={`fir-btn-next-${f.name}`}
                      title="Passer à la question suivante (Entrée)"
                    >
                      Continuer <span aria-hidden="true">→</span>
                    </button>
                    <span className="fir-card-hint">ou appuyez sur <kbd>Entrée</kbd></span>
                  </div>
                )}
              </div>
            );
          })}

          {allAnswered && (
            <div className="fir-stream-end" data-testid="fir-stream-end">
              <p>✅ Toutes les questions visibles ont été répondues.</p>
              <button
                className="win-btn"
                style={{ fontWeight: "bold" }}
                onClick={async () => {
                  await persist();
                  await axios.post(`${API}/fir/${fir.id}/complete`);
                  if (onCompleted) onCompleted();
                  if (onClose) onClose();
                }}
                data-testid="fir-btn-complete"
              >Terminer l'entrevue</button>
            </div>
          )}
        </div>

        <div className="fir-actions">
          <button className="win-btn" onClick={quit} data-testid="fir-btn-quit">
            Quitter et reprendre plus tard
          </button>
          {currentItem?.type === "field" && !allAnswered && (
            <button
              type="button"
              className="fir-btn-next"
              onClick={goNext}
              data-testid="fir-btn-next-sticky"
              title="Passer à la question suivante"
              style={{ fontSize: 14, padding: "9px 22px" }}
            >
              Continuer <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
